/*
 * The strategy list.
 *
 * A card per rule, and the two controls that matter on each: whether it is
 * running, and whether it is running for real. Those are kept visually apart —
 * the arm switch is a switch, and moving to live is a separate, deliberate
 * action inside the strategy itself — because they are not the same decision
 * and a UI that treats them alike invites the wrong one.
 */

import { useState } from "react";
import { Link } from "react-router-dom";

import {
  useCreateStrategy,
  useResumeDay,
  useSetEnabled,
  useStatus,
  useStrategies,
} from "../api/hooks";
import type { Strategy } from "../api/types";
import { IconEdit, IconPlus, IconWarning } from "../components/Icons";
import { Modal } from "../components/Modal";
import { useToast } from "../components/Toast";
import {
  KIND_BLURB,
  KIND_LABEL,
  clockTime,
  rupees,
} from "../lib/format";
import { StrategyForm, type FormState, blankForm, toPayload } from "./StrategyForm";

export function StrategiesPage() {
  const strategies = useStrategies();
  const status = useStatus();
  const [creating, setCreating] = useState(false);

  const rows = strategies.data ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Strategies</h1>
          <p>
            A strategy is one rule, a handful of instruments and a size. It starts on
            paper and disabled — nothing about it is live until you say so twice.
          </p>
        </div>
        <div className="page-actions">
          <button className="btn primary" onClick={() => setCreating(true)}>
            <IconPlus size={15} /> New strategy
          </button>
        </div>
      </div>

      {!status.data?.connected && (
        <div className="banner info">
          <span className="ico"><IconWarning size={17} /></span>
          <div className="body">
            <strong>No Zerodha account yet</strong>
            <p>
              You can write and backtest strategies without one, but nothing will run
              until an account is connected in Settings.
            </p>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="card">
          <div className="empty">
            <strong>No strategies yet</strong>
            Start with one rule on one stock, on paper, and watch it for a week.
          </div>
        </div>
      ) : (
        <div className="grid cols-2">
          {rows.map((s) => (
            <StrategyCard key={s.id} strategy={s} />
          ))}
        </div>
      )}

      {creating && <NewStrategyDialog onClose={() => setCreating(false)} />}
    </>
  );
}

function StrategyCard({ strategy }: { strategy: Strategy }) {
  const setEnabled = useSetEnabled();
  const resume = useResumeDay();
  const toast = useToast();

  return (
    <div className="card">
      <div className="card-head">
        <div style={{ minWidth: 0 }}>
          <h2>
            <Link to={`/strategies/${strategy.id}`} style={{ color: "inherit" }}>
              {strategy.name}
            </Link>
          </h2>
          <div className="sub">
            {KIND_LABEL[strategy.kind]} · {strategy.interval} · {strategy.product}
          </div>
        </div>
        <div className="row tight">
          <span className={`pill ${strategy.mode}`}>{strategy.mode}</span>
          <button
            className={`switch ${strategy.isEnabled ? "on" : ""}`}
            aria-label={strategy.isEnabled ? "Disable" : "Enable"}
            aria-pressed={strategy.isEnabled}
            disabled={setEnabled.isPending}
            onClick={() =>
              setEnabled.mutate(
                { id: strategy.id, isEnabled: !strategy.isEnabled },
                {
                  onSuccess: () =>
                    toast.ok(
                      strategy.isEnabled
                        ? `${strategy.name} is off.`
                        : `${strategy.name} is running.`,
                    ),
                  onError: (e) => toast.fail(e),
                },
              )
            }
          />
        </div>
      </div>

      <div className="card-body col" style={{ gap: 11 }}>
        <p className="small faint" style={{ margin: 0 }}>
          {KIND_BLURB[strategy.kind]}
        </p>

        <div className="row" style={{ gap: 14 }}>
          <Figure k="Per trade" v={rupees(strategy.perTradeRupees)} />
          <Figure k="Max open" v={String(strategy.maxPositions)} />
          <Figure k="Day limit" v={rupees(strategy.dailyLossRupees)} />
          <Figure
            k="Stop / target"
            v={`${strategy.stopLossPct ?? "—"}% / ${strategy.targetPct ?? "—"}%`}
          />
        </div>

        <div className="row tight">
          {strategy.watches.length === 0 ? (
            <span className="pill warn">No instruments</span>
          ) : (
            strategy.watches.slice(0, 6).map((w) => (
              <span className="pill" key={w.instrumentToken}>
                {w.tradingsymbol}
              </span>
            ))
          )}
          {strategy.watches.length > 6 && (
            <span className="pill off">+{strategy.watches.length - 6} more</span>
          )}
        </div>

        {strategy.lastError && (
          <div className="banner warn" style={{ margin: 0 }}>
            <div className="body">
              <strong>Stopped</strong>
              <p>{strategy.lastError}</p>
            </div>
            <button
              className="btn sm"
              disabled={resume.isPending}
              onClick={() =>
                resume.mutate(strategy.id, {
                  onSuccess: () => toast.ok("Cleared for today."),
                  onError: (e) => toast.fail(e),
                })
              }
            >
              Resume today
            </button>
          </div>
        )}

        <div className="row small faint" style={{ justifyContent: "space-between" }}>
          <span>
            Entry {strategy.entryFrom}–{strategy.entryTo}
            {strategy.squareOffAt ? ` · square off ${strategy.squareOffAt}` : ""}
          </span>
          <span>
            {strategy.lastRunAt ? `Last run ${clockTime(strategy.lastRunAt)}` : "Not run yet"}
          </span>
        </div>

        {/* Spelled out as buttons. The card title was a link and nothing said
            so, which left editing a strategy and adding instruments looking
            like things the application could not do. */}
        <div className="row tight" style={{ borderTop: "1px solid var(--border)", paddingTop: 11 }}>
          <Link className="btn sm" to={`/strategies/${strategy.id}`}>
            <IconEdit size={14} /> Edit rule &amp; limits
          </Link>
          <Link className="btn sm" to={`/strategies/${strategy.id}#instruments`}>
            <IconPlus size={14} /> Add companies
          </Link>
          <Link className="btn sm ghost" to={`/strategies/${strategy.id}#backtest`}>
            Backtest
          </Link>
        </div>
      </div>
    </div>
  );
}

function Figure({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div className="tiny faint">{k}</div>
      <div className="num" style={{ fontWeight: 600, fontSize: 13 }}>{v}</div>
    </div>
  );
}

function NewStrategyDialog({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState<FormState>(blankForm());
  const create = useCreateStrategy();
  const toast = useToast();

  const submit = () => {
    create.mutate(toPayload(form), {
      onSuccess: (s) => {
        toast.ok(`${s.name} created, on paper and switched off.`);
        onClose();
      },
      onError: (e) => toast.fail(e),
    });
  };

  return (
    <Modal
      title="New strategy"
      wide
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={submit} disabled={create.isPending}>
            {create.isPending ? <span className="spinner" /> : "Create"}
          </button>
        </>
      }
    >
      <StrategyForm value={form} onChange={setForm} />
      <p className="small faint mt">
        It will be created on paper and switched off. Add instruments to it, backtest
        it, and watch it run on paper before anything is real.
      </p>
    </Modal>
  );
}
